import {css} from 'aphrodite';
import React from 'react';

import isDiscoverAnalysisEnabled from 'analysis-tool/isDiscoverAnalysisEnabled';
import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  ProcessesFeature,
  RegistryFeature,
  V2RequestsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {REQUESTS_V2_SIDEPANEL_TAB_KEY, Section, SECTIONS} from 'constants/NavConstants';
import DevToolsIcon from 'dev/DevToolsIcon';
import navigateToURL from 'lib/navigateToURL';
import {isAISidepanelEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {canRunScientificAIviaTAC} from 'model-hub/permissions/ModelHubPermissions';
import CreateIcon from 'nav/navbar/CreateIcon';
import HomeIcon from 'nav/navbar/HomeIcon';
import {navBarContentsStyles} from 'nav/navbar/NavBarContents';
import NavBarIcon, {NavBarIconProps} from 'nav/navbar/NavBarIcon';
import RegistryIcon from 'nav/navbar/RegistryIcon';
import UserIcon from 'nav/navbar/UserIcon';
import {shouldShowNewHomepage} from 'nav/sidepanel/homepage/HomepageVisibility';
import {useOmniboxAvailable} from 'omnibox/OmniboxOpenControl';
import ChecklistMenuIcon from 'onboarding/ChecklistMenuIcon';
import {shouldShowOnboardingChecklistsIcon} from 'onboarding/OnboardingUtils';
import UpgradeAccountModal, {ENTERPRISE_FEATURES_KEYS} from 'start/upgrade-account/UpgradeAccountModal';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import {ElementMouseEvent} from 'util/Types';

/**
 * Figure out how many items of size {@itemSize} fit in a linear space. Any items that fit will be partitioned
 * into `visible`, any items that do not will be partitioned into `overflow`.
 *
 * This function allows for a 'fixed' amount of items that will never collapse.
 * @param collapsible List of collapsible items.
 * @param fixedCount The number of 'fixed' items that never collapse
 * @param itemSize The size of an individual item.
 * @param totalSize The total size available for items.
 * @package
 */
export function partitionBySize<T>({
  collapsible,
  fixedCount = 0,
  itemSize,
  totalSize,
}: {
  collapsible: Array<T>;
  fixedCount?: number;
  itemSize: number;
  totalSize: number;
}): {visible: Array<T>; overflow: Array<T>} {
  // Calculate how much space the top and bottom items and the overflow menu take up together.
  const fixedSpaceConsumed = fixedCount * itemSize;
  // Calculate how much space we have for collapsible items.
  const availableSpace = totalSize - fixedSpaceConsumed;
  if (availableSpace < itemSize) {
    // If we have no available space, everything must collapse.
    return {visible: [], overflow: collapsible};
  } else {
    // If we have some available space, figure out how many items we can fit in that space.
    const maxVisibleItems = Math.floor(availableSpace / itemSize);
    // Special case: if we have exactly enough space for all the items, never overflow exactly one item.
    if (maxVisibleItems === collapsible.length) {
      return {visible: collapsible, overflow: []};
    }
    return {
      // Otherwise, show only as many items as we have space for, overflowing the rest.
      visible: collapsible.slice(0, maxVisibleItems - 1),
      overflow: collapsible.slice(maxVisibleItems - 1),
    };
  }
}

interface UseOverflowMenuProps {
  currentUser: EntityStoreUser;
  isCreateMenuOpen: boolean;
  isHomeIconActive: boolean;
  isUserIconActive: boolean;
  height: number;
  onSectionClick: (section: Section, e?: ElementMouseEvent) => void;
  section: Section | null;
}

interface NavBarIconGroups {
  topIcons: Array<JSX.Element>;
  collapsibleIconsProps: Array<
    Pick<NavBarIconProps, 'className' | 'icon' | 'isActive' | 'onSectionClick' | 'section'>
  >;
  overflowIconsProps: Array<
    Pick<NavBarIconProps, 'className' | 'icon' | 'isActive' | 'onSectionClick' | 'section'>
  >;
  bottomIcons: Array<JSX.Element>;
}

/**
 * Decides which navbar icons should be shown.
 *
 * As the navbar grows and shrinks in height, this hook shifts a subset of icons into an overflow menu. Icons
 * eligible for overflow must be regular `NavIcon`. As a result, a subset of icons at the top and bottom of
 * the navbar are 'fixed' to the top or bottom. The bottommost 'fixed' icon at the top is `RegistryIcon`, and
 * the topmost 'fixed' icon at the bottom is `DevToolsIcon`.
 * @param currentUser The user viewing the navbar
 * @param isCreateMenuOpen Whether the global create menu is open
 * @param isHomeIconActive Whether the home drawer is active
 * @param isUserIconActive Whether the user menu is open
 * @param height The height of the nav bar in pixels
 * @param onSectionClick A function to be called when a section icon is clicked
 * @param section The currently active section, if any
 */
export default function useOverflowingNavBarIcons({
  currentUser,
  isCreateMenuOpen,
  isHomeIconActive,
  isUserIconActive,
  height,
  onSectionClick,
  section,
}: UseOverflowMenuProps): NavBarIconGroups {
  // When the omnibox is mounted, the search icon opens it (a dialog) rather than the search
  // sidepanel, so it should advertise button rather than tab semantics.
  const omniboxAvailable = useOmniboxAvailable();

  const fixedTopIcons: Array<{icon: JSX.Element; show: boolean}> = [
    {
      icon: (
        <HomeIcon
          currentUser={currentUser}
          icon="benchling"
          isActive={isHomeIconActive}
          onHomeIconClick={(section, e) => {
            trackEvent(
              {
                module: ANALYTICS_MODULE.NAVIGATION,
                action: 'home_icon_clicked',
                actionCategory: ACTION_CATEGORY.READ,
              },
              {newHomepageEnabled: shouldShowNewHomepage()}
            );
            onSectionClick(section, e);
          }}
        />
      ),
      show: true,
    },
    {
      icon: (
        <NavBarIcon
          icon="sparkle"
          isActive={section === SECTIONS.AI}
          onSectionClick={onSectionClick}
          section={SECTIONS.AI}
        />
      ),
      show: isAISidepanelEnabled(),
    },
    {
      icon: (
        <NavBarIcon
          icon={ModelTypeConstants.SOURCE}
          isActive={
            section === SECTIONS.PROJECT_ITEMS ||
            section === SECTIONS.PROJECTS ||
            section === SECTIONS.TEMPLATE_COLLECTIONS
          }
          onSectionClick={onSectionClick}
          section={SECTIONS.PROJECTS}
          className="_test-navDropdownMenu-item-projects"
        />
      ),
      show: true,
    },
    {
      icon: (
        <NavBarIcon
          icon={ModelTypeConstants.STUDY}
          isActive={section === SECTIONS.STUDIES}
          onSectionClick={onSectionClick}
          section={SECTIONS.STUDIES}
        />
      ),
      show: BenchlingConfig.STUDIES_ENABLE,
    },
    {
      icon: (
        <NavBarIcon
          icon="search"
          isActive={section === SECTIONS.SEARCH}
          onSectionClick={onSectionClick}
          section={SECTIONS.SEARCH}
          opensDialog={omniboxAvailable}
        />
      ),
      show: true,
    },
    {
      icon: <CreateIcon isCreateMenuOpen={isCreateMenuOpen} />,
      show: true,
    },
    {icon: <div className={css(navBarContentsStyles.spacer)} />, show: true},
    {
      icon: (
        <RegistryIcon
          currentUser={currentUser}
          onSectionClick={onSectionClick}
          isActive={section === SECTIONS.REGISTRIES}
          section={SECTIONS.REGISTRIES}
        />
      ),
      show: isFeatureEnabled({feature: RegistryFeature.REGISTRY_DISCOVER}),
    },
  ];
  // visibleTopIcons are fixed at the top of the screen, and never go into the overflow menu
  const visibleTopIcons: Array<JSX.Element> = fixedTopIcons.filter(({show}) => show).map(({icon}) => icon);

  const allCollapsibleIcons: Array<{
    iconProps: Pick<NavBarIconProps, 'className' | 'icon' | 'isActive' | 'onSectionClick' | 'section'>;
    show: boolean;
  }> = [
    {
      iconProps: {
        icon: ModelTypeConstants.LOCATION,
        isActive: section === SECTIONS.STORAGE,
        onSectionClick,
        section: SECTIONS.STORAGE,
      },
      show: isFeatureEnabled({feature: InventoryFeature.INVENTORY_DISCOVER}),
    },
    {
      iconProps: {
        icon: 'hexagon-nodes',
        isActive: section === SECTIONS.MODEL_HUB,
        onSectionClick: () => {
          navigateToURL('/model-hub');
        },
        section: SECTIONS.MODEL_HUB,
      },
      show: BenchlingConfig.MODEL_HUB_ENABLE_MODEL_HUB_UI && canRunScientificAIviaTAC(),
    },
    {
      iconProps: {
        icon: 'workflow',
        isActive: section === SECTIONS.WORKFLOWS,
        onSectionClick,
        section: SECTIONS.WORKFLOWS,
      },
      show: BenchlingConfig.LEGACY_WORKFLOWS_ENABLE,
    },
    {
      iconProps: {
        icon: 'workflow',
        className: css(UpgradeAccountModal.hasRequested() && navBarContentsStyles.disabledIcon),
        isActive: section === SECTIONS.WORKFLOWS,
        onSectionClick: () => {
          UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.WORKFLOWS);
        },
        section: SECTIONS.WORKFLOWS,
      },
      show: !BenchlingConfig.LEGACY_WORKFLOWS_ENABLE && UpgradeAccountModal.isEnabled(currentUser),
    },
    {
      iconProps: {
        icon: 'process',
        isActive: section === SECTIONS.PROCESSES,
        onSectionClick,
        section: SECTIONS.PROCESSES,
      },
      show: isFeatureEnabled({feature: ProcessesFeature.PROCESSES_DISCOVER}),
    },
    {
      iconProps: {
        icon: 'request',
        isActive: section === SECTIONS.REQUESTS,
        onSectionClick,
        section: SECTIONS.REQUESTS,
      },
      show: BenchlingConfig.ENABLE_REQUESTS,
    },
    {
      iconProps: {
        icon: 'request',
        className: css(UpgradeAccountModal.hasRequested() && navBarContentsStyles.disabledIcon),
        isActive: section === SECTIONS.REQUESTS,
        onSectionClick: () => {
          UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.REQUESTS);
        },
        section: SECTIONS.REQUESTS,
      },
      show: !BenchlingConfig.ENABLE_REQUESTS && UpgradeAccountModal.isEnabled(currentUser),
    },
    {
      iconProps: {
        icon: 'request_v2_submission',
        isActive:
          section != null &&
          [SECTIONS.REQUESTS_V2_SUBMISSIONS, SECTIONS.REQUESTS_V2_DEFINITIONS].includes(section),
        onSectionClick,
        section: localStorage.getItem(REQUESTS_V2_SIDEPANEL_TAB_KEY)
          ? (localStorage.getItem(REQUESTS_V2_SIDEPANEL_TAB_KEY) as Section)
          : SECTIONS.REQUESTS_V2_DEFINITIONS,
      },
      show:
        BenchlingConfig.REQUESTS_V2_ENABLE &&
        isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}),
    },
    {
      iconProps: {
        icon: 'analyze',
        isActive: section === SECTIONS.ANALYTICS,
        onSectionClick,
        section: SECTIONS.ANALYTICS,
      },
      show: isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DISCOVER}) || isDiscoverAnalysisEnabled(),
    },
    {
      iconProps: {
        icon: 'external-files',
        isActive:
          section === SECTIONS.TFI || section === SECTIONS.BENCHLING_APPS || section === SECTIONS.CONNECTIONS,
        onSectionClick,
        section: isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})
          ? SECTIONS.CONNECTIONS
          : SECTIONS.TFI,
      },
      show:
        BenchlingConfig.TFI_ENABLE ||
        isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER}),
    },
    {
      iconProps: {
        icon: 'core-object',
        isActive: section === SECTIONS.JELLYFISH,
        onSectionClick,
        section: SECTIONS.JELLYFISH,
      },
      show: BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE,
    },
  ];
  const visibleCollapsibleIcons: Array<
    Pick<NavBarIconProps, 'className' | 'icon' | 'isActive' | 'onSectionClick' | 'section'>
  > = allCollapsibleIcons.filter(({show}) => show).map(({iconProps}) => iconProps);

  const fixedBottomIcons: Array<{icon: JSX.Element; show: boolean}> = [
    {icon: <DevToolsIcon withUserMenuOffset={true} />, show: window.debug ?? false},
    {
      icon: <ChecklistMenuIcon shouldAlignBottom={!window.debug} shouldShowNotification={true} />,
      show: shouldShowOnboardingChecklistsIcon(),
    },
    {
      icon: <UserIcon currentUser={currentUser} isActive={isUserIconActive} />,
      show: true,
    },
  ];
  // visibleBottomIcons are fixed at the bottom of the screen, and never go into the overflow menu
  const visibleBottomIcons: Array<JSX.Element> = fixedBottomIcons
    .filter(({show}) => show)
    .map(({icon}) => icon);

  const {visible, overflow} = partitionBySize({
    collapsible: visibleCollapsibleIcons,
    fixedCount: visibleTopIcons.length + visibleBottomIcons.length,
    itemSize: ITEM_SIZE_SIDE_NAVBAR,
    totalSize: height,
  });

  return {
    topIcons: visibleTopIcons,
    collapsibleIconsProps: visible,
    overflowIconsProps: overflow,
    bottomIcons: visibleBottomIcons,
  };
}
